import React, { useEffect, useState } from 'react'
import { fetchChecklist, submitTicketAnswers, buildAnswersPayload, isNoLabel } from './remoteUnlockApi'
import ChecklistFields, { missingAnswers, answerSummary } from './ChecklistFields'
import { RuModal } from './RuPrimitives'
import './remoteUnlock.css'

/**
 * Stage 1 — Eligibility checklist (L1), opened after the ticket is created when
 * the sub-category has enable_checklist = true and the agent has 'l1' access.
 *   step 'form'    → all visible questions are mandatory
 *   step 'summary' → submitted answers + Review Checklist / Proceed
 */
export default function Stage1ChecklistModal({
  open, fetchJson, ticket, subCategorySlug, subCategoryName, canDoL2, onProceed, onSkip,
}) {
  const [step, setStep] = useState('form')
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [proceeding, setProceeding] = useState(false)
  const [submitted, setSubmitted] = useState(false)   // L1 answers already saved for this ticket

  useEffect(() => {
    if (!open || !subCategorySlug || !fetchJson) return
    let active = true
    setStep('form'); setAnswers({}); setShowErrors(false); setSubmitError(''); setSubmitted(false)
    setLoading(true); setLoadError('')
    fetchChecklist(fetchJson, 'l1', subCategorySlug)
      .then(qs => { if (active) setQuestions(qs) })
      .catch(err => { if (active) setLoadError(err?.message || 'Failed to load checklist.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [open, subCategorySlug, fetchJson])

  const summary = answerSummary(questions, answers)
  const allOk = summary.every(s => !s.isChoice || !isNoLabel(s.label))

  const handleSubmit = async () => {
    if (missingAnswers(questions, answers).length) { setShowErrors(true); return }
    setSubmitting(true); setSubmitError('')
    try {
      await submitTicketAnswers(fetchJson, ticket.ticket_id, 'l1', buildAnswersPayload(questions, answers))
      setSubmitted(true)
      setStep('summary')
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save checklist.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleProceed = async () => {
    setProceeding(true)
    try { await onProceed?.({ allOk, summary }) } finally { setProceeding(false) }
  }

  if (!open) return null

  if (step === 'summary') {
    return (
      <RuModal
        tone={allOk ? 'success' : 'warning'}
        title={allOk ? '✓ Eligible for remote session' : 'Checklist submitted — some criteria not met'}
        subtitle={`Ticket ${ticket?.ticket_number ? `#${ticket.ticket_number}` : ''} · ${subCategoryName || ''}`}
        footer={<>
          <button type="button" className="ru-btn ru-btn-ghost" onClick={() => setStep('form')} disabled={proceeding}>
            Review Checklist
          </button>
          <button type="button" className="ru-btn ru-btn-primary" onClick={handleProceed} disabled={proceeding}>
            {proceeding ? 'Please wait…' : canDoL2 ? 'Proceed to Step 2' : 'Proceed — Transfer to L2'}
          </button>
        </>}
      >
        <div className={`ru-summary ${allOk ? 'ok' : 'warn'}`}>
          <div className="ru-summary-title">Submitted answers</div>
          <div className="ru-answer-list">
            {summary.map((s, i) => (
              <div key={s.id} className="ru-answer-row">
                <span className="ru-answer-num">{i + 1}</span>
                <span className="ru-answer-q">{s.text}</span>
                <span className={`ru-chip ${!s.isChoice ? 'neutral' : isNoLabel(s.label) ? 'bad' : 'ok'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="ru-proceed-note">
          {canDoL2
            ? 'You have L2 access — Proceed opens the ticket on the Remote Session tab so you can run Step 2 on this call.'
            : 'Proceed transfers this ticket to the L2 queue; an L2 agent will run the remote unlock.'}
        </p>
      </RuModal>
    )
  }

  return (
    <RuModal
      title="Stage 1 — Eligibility Check"
      subtitle="All questions are mandatory."
      footer={<>
        <button type="button" className="ru-btn ru-btn-ghost" onClick={() => onSkip?.({ submitted })} disabled={submitting}>Cancel</button>
        <button
          type="button" className="ru-btn ru-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || loading || !!loadError || questions.length === 0}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </>}
    >
      {loading && <div className="ru-muted">Loading checklist…</div>}
      {!loading && loadError && <div className="ru-error">{loadError}</div>}
      {!loading && !loadError && questions.length === 0 && (
        <div className="ru-muted">No checklist is configured for this sub-category.</div>
      )}
      {!loading && (
        <ChecklistFields
          questions={questions} answers={answers} showErrors={showErrors} namePrefix="l1"
          onChange={(qid, v) => { setShowErrors(false); setAnswers(a => ({ ...a, [qid]: v })) }}
        />
      )}
      {submitError && <div className="ru-error">{submitError}</div>}
    </RuModal>
  )
}
